import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useInstitutionMembershipMutations } from "../hooks";
import type { MembershipWithPerson } from "../types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  membership: MembershipWithPerson;
  institutionId: string;
}

export function RemoveMemberDialog({ isOpen, onClose, membership, institutionId }: Props) {
  const [leftAt, setLeftAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const { remove } = useInstitutionMembershipMutations(institutionId);

  const person = membership.minister ?? membership.institution_member;
  const name = person?.full_name ?? "este miembro";

  async function handleSave() {
    setSaving(true);
    try {
      await remove(membership.id, leftAt, notes.trim() || null);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !saving && !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Dar de baja</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Dar de baja a{" "}
            <span className="font-medium text-foreground">{name}</span> de esta institución.
          </p>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Fecha de baja</label>
            <input
              type="date"
              value={leftAt}
              onChange={(e) => setLeftAt(e.target.value)}
              disabled={saving}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Motivo <span className="text-muted-foreground">(opcional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              disabled={saving}
              placeholder="Ej: renuncia voluntaria, traslado…"
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            Dar de baja
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
